/**
 * Permission Utilities for Frontend
 *
 * Provides utilities for checking user permissions and managing
 * permission-based UI features in the frontend application.
 */

import { FeaturePermission, ALL_FEATURE_PERMISSIONS } from '../constants/featurePermissions';

// User permission context interface
export interface UserPermissionContext {
  featurePermissions: FeaturePermission[];
  role: string;
}

// User with feature permissions structure
export interface UserWithFeaturePermissions {
  role: string;
  featurePermissions?: FeaturePermission[];
}

/**
 * Check if user has a specific feature permission
 */
export const hasFeaturePermission = (
  userPermissions: FeaturePermission[],
  requiredFeature: FeaturePermission
): boolean => {
  return Array.isArray(userPermissions) && userPermissions.includes(requiredFeature);
};

/**
 * Check if user has any of the required feature permissions
 */
export const hasAnyFeaturePermission = (
  userPermissions: FeaturePermission[],
  requiredFeatures: FeaturePermission[]
): boolean => {
  if (!Array.isArray(userPermissions) || !Array.isArray(requiredFeatures)) {
    return false;
  }
  return requiredFeatures.some(feature => userPermissions.includes(feature));
};

/**
 * Check if user has all required feature permissions
 */
export const hasAllFeaturePermissions = (
  userPermissions: FeaturePermission[],
  requiredFeatures: FeaturePermission[]
): boolean => {
  if (!Array.isArray(userPermissions) || !Array.isArray(requiredFeatures)) {
    return false;
  }
  return requiredFeatures.every(feature => userPermissions.includes(feature));
};

/**
 * Get user's permission level for a feature category
 */
export const getPermissionLevel = (
  userPermissions: FeaturePermission[],
  category: string
): 'none' | 'read' | 'write' => {
  const readFeature = `${category}_read` as FeaturePermission;
  const writeFeature = `${category}_write` as FeaturePermission;

  if (hasFeaturePermission(userPermissions, writeFeature)) {
    return 'write';
  }
  if (hasFeaturePermission(userPermissions, readFeature)) {
    return 'read';
  }
  return 'none';
};

/**
 * Group user permissions by category
 */
export const groupPermissionsByCategory = (
  userPermissions: FeaturePermission[]
): Record<string, { read: boolean; write: boolean; update: boolean; delete: boolean; special?: string[] }> => {
  const categories: Record<string, { read: boolean; write: boolean; update: boolean; delete: boolean; special?: string[] }> = {};

  userPermissions.forEach(permission => {
    const [category, access] = permission.split('_');

    if (!categories[category]) {
      categories[category] = { read: false, write: false, update: false, delete: false, special: [] };
    }

    if (access === 'read') {
      categories[category].read = true;
    } else if (access === 'write') {
      categories[category].write = true;
    } else if (access === 'update') {
      categories[category].update = true;
    } else if (access === 'delete') {
      categories[category].delete = true;
    } else {
      // Special permissions like 'assign', 'export'
      if (!categories[category].special) {
        categories[category].special = [];
      }
      categories[category].special!.push(access);
    }
  });

  return categories;
};

/**
 * Check if user has administrative permissions
 */
export const isAdmin = (userPermissions: FeaturePermission[]): boolean => {
  const adminFeatures: FeaturePermission[] = [
    'admin_read',
    'admin_write',
    'admin_update',
    'admin_delete',
    'company_write',
    'company_update',
    'company_delete',
    'users_write',
    'users_update',
    'users_delete'
  ];

  return hasAnyFeaturePermission(userPermissions, adminFeatures);
};

/**
 * Check if user can manage finances
 */
export const canManageFinances = (userPermissions: FeaturePermission[]): boolean => {
  const financialFeatures: FeaturePermission[] = [
    'payments_write',
    'payments_update',
    'payments_delete',
    'invoices_write',
    'invoices_update',
    'invoices_delete'
  ];

  return hasAnyFeaturePermission(userPermissions, financialFeatures);
};

/**
 * Check if user can manage properties
 */
export const canManageProperties = (userPermissions: FeaturePermission[]): boolean => {
  const propertyFeatures: FeaturePermission[] = [
    'properties_write',
    'properties_update',
    'properties_delete',
    'rentalunit_write',
    'rentalunit_update',
    'rentalunit_delete'
  ];

  return hasAnyFeaturePermission(userPermissions, propertyFeatures);
};

/**
 * Check if user can manage maintenance
 */
export const canManageMaintenance = (userPermissions: FeaturePermission[]): boolean => {
  const maintenanceFeatures: FeaturePermission[] = [
    'maintenance_write',
    'maintenance_update',
    'maintenance_delete',
    'maintenance_assign'
  ];

  return hasAnyFeaturePermission(userPermissions, maintenanceFeatures);
};

/**
 * Check if user can view analytics
 */
export const canViewAnalytics = (userPermissions: FeaturePermission[]): boolean => {
  const analyticsFeatures: FeaturePermission[] = [
    'analytics_read',
    'reports_read'
  ];

  return hasAnyFeaturePermission(userPermissions, analyticsFeatures);
};

/**
 * Get readable permission description
 */
export const getPermissionDescription = (permission: FeaturePermission): string => {
  const descriptions: Record<FeaturePermission, string> = {
    'properties_read': 'View Properties',
    'properties_write': 'Create Properties',
    'properties_update': 'Update Properties',
    'properties_delete': 'Delete Properties',
    'properties_transfer': 'Transfer Properties',
    'properties_takeover': 'Take Over Properties',
    'properties_public_listing': 'Publicly List Properties',
    'properties_sharedcharges_read': 'View Shared Charges',
    'properties_sharedcharges_write': 'Manage Shared Charges',
    'rentalunit_read': 'View Rental Units',
    'rentalunit_write': 'Create Rental Units',
    'rentalunit_update': 'Update Rental Units',
    'rentalunit_delete': 'Delete Rental Units',
    'tenants_read': 'View Tenants',
    'tenants_write': 'Create Tenants',
    'tenants_update': 'Update Tenants',
    'tenants_delete': 'Delete Tenants',
    'tenants_allow_multiple': 'Allow Multiple Tenants per Rental Unit',
    'bookings_read': 'View Bookings',
    'bookings_write': 'Create Bookings',
    'bookings_update': 'Update Bookings',
    'bookings_delete': 'Delete Bookings',
    'payments_read': 'View Payments',
    'payments_write': 'Record Payments',
    'payments_update': 'Update Payments',
    'payments_delete': 'Delete Payments',
    'invoices_read': 'View Invoices',
    'invoices_write': 'Create Invoices',
    'invoices_update': 'Update Invoices',
    'invoices_delete': 'Delete Invoices',
    'invoices_change_vendor': 'Change Payment Gateway Vendor',
    'invoices_bulk_generation': 'Bulk Generate Invoices',
    'invoices_propertywise': 'View Property Wise Invoices',
    'maintenance_read': 'View Maintenance',
    'maintenance_write': 'Create Maintenance',
    'maintenance_update': 'Update Maintenance',
    'maintenance_delete': 'Delete Maintenance',
    'maintenance_assign': 'Assign Maintenance',
    'maintenance_closecode': 'Manage Maintenance Close Code',
    'inventory_category_write': 'Create Inventory Categories',
    'inventory_category_update': 'Update Inventory Categories',
    'inventory_category_delete': 'Delete Inventory Categories',
    'inventory_items_create': 'Create Inventory Items',
    'inventory_items_update': 'Update Inventory Items',
    'inventory_items_read': 'View Inventory Items',
    'inventory_items_delete': 'Delete Inventory Items',
    'expenses_read': 'View Expenses',
    'expenses_write': 'Create Expenses',
    'expenses_update': 'Update Expenses',
    'expenses_delete': 'Delete Expenses',
    'company_read': 'View Company Settings',
    'company_write': 'Create Company Settings',
    'company_update': 'Update Company Settings',
    'company_delete': 'Delete Company Settings',
    'integrations_tab': 'Manage Integrations',
    'users_read': 'View Users',
    'users_write': 'Add Users',
    'users_update': 'Update Users',
    'users_delete': 'Remove Users',
    'analytics_read': 'View Analytics',
    'reports_read': 'View Reports',
    'analytics_export': 'Export Analytics',
    'admin_read': 'View Admin Panel',
    'admin_write': 'Create Admin Settings',
    'admin_update': 'Update Admin Settings',
    'admin_delete': 'Delete Admin Settings',
    'notifications_read': 'View Notifications',
    'notifications_write': 'Send Notifications',
    'notifications_update': 'Update Notifications',
    'notifications_delete': 'Delete Notifications',
    'pg_accounts_write': 'Manage Payment Gateway Accounts',
    'pg_accounts_read': 'View Payment Gateway Accounts',
    'pg_accounts_update': 'Update Payment Gateway Accounts',
    'pg_accounts_delete': 'Delete Payment Gateway Accounts',
    'pg_accounts_reload': 'Reload Payment Gateway Accounts',
    'broadcast_read': 'View Broadcasts',
    'broadcast_write': 'Create Broadcasts',
    'broadcast_update': 'Update Broadcasts',
    'broadcast_delete': 'Delete Broadcasts',
    'company_catalogue': 'View Company Catalogue',
    'whatsapp_notifications': 'Send WhatsApp Notifications',
    'email_notifications': 'Send Email Notifications',
    'auto_billing': 'Automatic Invoice Generation',
    'invoice_due_reminder': 'Invoice Due Date Reminders',
    'invoice_late_fee': 'Invoice Late Fee Charges',
    'late_fee_stepup': 'Late Fee Step Up',
    'late_fee_retro_stepup': 'Late Fee Retro Step Up',
    'bulk_meter_recording': 'Bulk Meter Recording',
    'profit_loss_export': 'Export Profit & Loss Reports',
    'custom_css': 'Custom CSS Overrides',
    'catalogue_branding_removal': 'Remove Catalogue Branding',
    'custom_domain': 'Custom Domain',
  };

  return descriptions[permission] || permission.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
};

/**
 * Validate if permission array is valid
 */
export const validatePermissions = (permissions: unknown): permissions is FeaturePermission[] => {
  if (!Array.isArray(permissions)) {
    return false;
  }

  return permissions.every(permission =>
    typeof permission === 'string' &&
    ALL_FEATURE_PERMISSIONS.includes(permission as FeaturePermission)
  );
};

/**
 * Create permission context for components
 */
export const createPermissionContext = (
  userPermissions: FeaturePermission[],
  role: string
): UserPermissionContext => {
  return {
    featurePermissions: userPermissions || [],
    role
  };
};
